import { useCallback } from 'react';
import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate, TranslateHtml } from '@change-corgi/core-react/i18n';

import { useIsVideoFirstArm, useSupporterVoicesVideoFirstTrackingProps } from '#src/app/pages/actions/shared/hooks';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { TermsDisclaimer } from '../../../TermsDisclaimer';

import { useUploadProcess } from './hooks/useUploadProcess';

export const Upload: React.FC = () => {
	const {
		data: { isUploading },
		actions: { startUploadProcess, prepareCamera },
	} = useUploadProcess();
	const { track } = useCampaignTracking();
	const isVideoFirstArm = useIsVideoFirstArm();
	const trackingProps = useSupporterVoicesVideoFirstTrackingProps();

	const handleClickSubmit = useCallback(() => {
		track('campaign_action_create_video_submit_click', trackingProps);
		startUploadProcess();
	}, [track, startUploadProcess, trackingProps]);

	return (
		<div className="flex flex-col items-center gap-y-4">
			{/* The design runs this screen a step quieter than production: 14px here rather than 16px. */}
			<Text as="p" variant="body" size={isVideoFirstArm ? 'small' : 'medium'} className="text-typography-weak">
				<Translate value="corgi.petition_actions.supporter_voice_video.submit.description" />
			</Text>
			<Button
				className="w-full"
				data-testid="upload-video"
				data-qa="upload-video"
				variant="primary"
				onClick={handleClickSubmit}
				loading={isUploading}
			>
				<Translate value="corgi.petition_actions.supporter_voice_video.submit.submit_button.label" />
			</Button>

			{/*
			 * `mb-6` stacks on top of the container's `gap-y-4`, putting 40px between this box and the
			 * notice below it where the design has 16px. Dropped on the video-first arms so the container's
			 * gap is the only spacing; control keeps today's margin.
			 */}
			<div
				className={cn('bg-fill-weaker dark:bg-background-raised w-full rounded-md p-4', {
					'mb-6': !isVideoFirstArm,
				})}
			>
				<Text as="p" variant="body" size="small" className="text-typography-strong">
					<TranslateHtml value="corgi.petition_actions.supporter_voice_video.submit.final_touches" />
				</Text>
			</div>

			{/*
			 * New here for the video-first arms: production shows the terms notice only while recording, so
			 * anyone who uploaded from their device reached submit without seeing it.
			 */}
			{isVideoFirstArm && <TermsDisclaimer tone="preview" />}

			{/*
			 * The prompt and its button read as one unit rather than two evenly-spaced children. `contents`
			 * leaves control untouched: the wrapper disappears and both stay in the container's gap.
			 */}
			<div className={isVideoFirstArm ? 'flex flex-col items-center gap-0.5 pt-2' : 'contents'}>
				<Text as="p" variant="body" size={isVideoFirstArm ? 'small' : 'medium'} className="text-typography-weak">
					<Translate value="corgi.petition_actions.supporter_voice_video.submit.retry_button.description" />
				</Text>
				<Button
					variant="tertiary"
					size={isVideoFirstArm ? 'small' : 'large'}
					onClick={() => {
						prepareCamera();
					}}
					data-testid="record-again"
					data-qa="record-again"
				>
					<Translate value="corgi.petition_actions.supporter_voice_video.submit.retry_button.label" />
				</Button>
			</div>
		</div>
	);
};
